import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { isMissing } from '../utils/format';

// Neutral mid-range starting positions for the sliders.
//
// A simulator needs *somewhere* to start, so unlike the display pages these
// values cannot simply be null. What changed is what they claim to be: these
// are explicitly a neutral starting point, not a description of the market.
// Previously the same numbers seeded the sliders AND were labelled "Current
// Market", so a user comparing their scenario against "today" was comparing it
// against constants typed into this file.
const NEUTRAL_STARTING_POSITION = {
  fed_funds_rate: 4.0,
  inflation_rate: 3.0,
  inflation_expectations: 2.5,
  unemployment_rate: 4.5,
  treasury_yield: 4.0,
  mbs_spread: 1.8,
  mbs_demand: 0.5,
  economic_momentum: 0.5,
  credit_spreads: 0.5,
  qt_intensity: 0.5,
  geopolitical_risk: 0.5,
};

const Simulator = ({ onBackToLanding, onViewMarketData }) => {
  const [simulationData, setSimulationData] = useState({
    ...NEUTRAL_STARTING_POSITION,
    time_horizon: "1_month",
    include_ai_analysis: true
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Live market conditions, once fetched. Null until then, and null forever if
  // the API cannot supply them.
  const [liveMarket, setLiveMarket] = useState(null);

  // Seed the sliders from live market data so the simulator starts at today's
  // conditions. If the fetch fails the neutral starting position stands and the
  // UI says the values are not live.
  useEffect(() => {
    let cancelled = false;

    const loadLiveConditions = async () => {
      try {
        const response = await apiService.getComprehensiveMarketData();
        const d = response?.data;
        if (!d || cancelled) return;

        const conditions = {
          fed_funds_rate: d.fed_funds_rate,
          inflation_rate: d.cpi_inflation,
          inflation_expectations: d.inflation_expectations,
          unemployment_rate: d.unemployment_rate,
          treasury_yield: d.treasury_10y,
          mbs_spread: d.mbs_spread,
          mbs_demand: d.mbs_demand,
          qt_intensity: d.qt_intensity,
          geopolitical_risk: d.geopolitical_risk,
        };

        // Keep only fields the API actually supplied.
        const available = Object.fromEntries(
          Object.entries(conditions).filter(([, value]) => !isMissing(value))
        );

        if (Object.keys(available).length === 0) return;

        setLiveMarket(available);
        setSimulationData(prev => ({ ...prev, ...available }));
      } catch (err) {
        console.error('Could not load live market conditions for the simulator:', err);
      }
    };

    loadLiveConditions();
    return () => { cancelled = true; };
  }, []);

  // Preset scenarios. "Current Market" is the live snapshot when one is
  // available; the rest are deliberately hypothetical what-ifs and are meant to
  // be fixed.
  const presetScenarios = {
    ...(liveMarket ? { current: { name: "Current Market", ...liveMarket } } : {}),
    recession: {
      name: "Recession Scenario",
      fed_funds_rate: 2.0,
      inflation_rate: 1.5,
      inflation_expectations: 1.8,
      unemployment_rate: 7.2,
      treasury_yield: 3.0,
      mbs_spread: 2.5,
      mbs_demand: 0.4,
      economic_momentum: 0.2,
      credit_spreads: 0.8,
      qt_intensity: 0.2,
      geopolitical_risk: 0.6
    },
    inflation_surge: {
      name: "High Inflation",
      fed_funds_rate: 7.0,
      inflation_rate: 6.5,
      inflation_expectations: 5.2,
      unemployment_rate: 4.5,
      treasury_yield: 6.2,
      mbs_spread: 2.2,
      mbs_demand: 0.3,
      economic_momentum: 0.7,
      credit_spreads: 0.6,
      qt_intensity: 0.8,
      geopolitical_risk: 0.4
    },
    optimistic: {
      name: "Economic Boom",
      fed_funds_rate: 4.0,
      inflation_rate: 2.1,
      inflation_expectations: 2.2,
      unemployment_rate: 3.0,
      treasury_yield: 3.8,
      mbs_spread: 1.4,
      mbs_demand: 0.9,
      economic_momentum: 0.9,
      credit_spreads: 0.2,
      qt_intensity: 0.3,
      geopolitical_risk: 0.1
    }
  };

  const runSimulation = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use environment variable for API base URL, fallback to localhost for development
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${apiBaseUrl}/api/simulate/custom-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(simulationData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setResults(data);
    } catch (err) {
      // Show error if backend is not available
      setError(`API Error: ${err.message}. Please check your connection or try again.`);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSimulationData(prev => ({
      ...prev,
      [field]: typeof value === 'boolean' ? value : (field === 'time_horizon' ? value : (parseFloat(value) || value))
    }));
  };

  const loadPresetScenario = (scenarioKey) => {
    const scenario = presetScenarios[scenarioKey];
    setSimulationData(prev => ({
      ...prev,
      ...scenario,
      time_horizon: prev.time_horizon,
      include_ai_analysis: prev.include_ai_analysis
    }));
    setResults(null); // Clear previous results
  };

  const exportResults = () => {
    if (!results) return;
    
    const exportData = {
      timestamp: new Date().toISOString(),
      scenario: simulationData,
      results: results
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ratelens-simulation-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Resets to live market conditions when we have them, otherwise to the
  // neutral starting position.
  const resetToDefaults = () => {
    setSimulationData({
      ...NEUTRAL_STARTING_POSITION,
      ...(liveMarket || {}),
      time_horizon: "1_month",
      include_ai_analysis: true
    });
    setResults(null);
    setError(null);
  };

  const compareToTodaysForecast = async () => {
    // Without live conditions there is no "today" to compare against. This used
    // to fall through to the hardcoded "Current Market" preset and present the
    // result as a comparison with the real market.
    if (!liveMarket) {
      setError('Live market conditions are unavailable, so there is nothing to compare against right now.');
      return;
    }

    const todaysData = {
      ...liveMarket,
      time_horizon: simulationData.time_horizon,
      include_ai_analysis: true
    };

    setLoading(true);
    setError(null);
    
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${apiBaseUrl}/api/simulate/custom-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(todaysData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const todaysResults = await response.json();
      
      // Create comparison result
      const comparisonResult = {
        ...results,
        comparison: {
          todays_forecast: todaysResults.data,
          difference: {
            rate_diff: results.data.projected_mortgage_rate - todaysResults.data.projected_mortgage_rate,
            rate_diff_bps: (results.data.projected_mortgage_rate - todaysResults.data.projected_mortgage_rate) * 100
          }
        }
      };
      
      setResults(comparisonResult);
    } catch (err) {
      setError(err.message);
      console.error('Comparison failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIndicator = (change) => {
    if (Math.abs(change) < 0.05) return { icon: '→', color: 'text-gray-600', trend: 'Stable' };
    if (change > 0) return { icon: '↗', color: 'text-red-600', trend: 'Rising' };
    return { icon: '↘', color: 'text-green-600', trend: 'Falling' };
  };

  const getRateExplanation = (rate, change) => {
    const absChange = Math.abs(change);
    if (absChange < 0.05) {
      return "Rate remains relatively stable with minimal market impact.";
    } else if (absChange < 0.25) {
      return change > 0 ? "Moderate upward pressure on rates." : "Moderate downward pressure on rates.";
    } else if (absChange < 0.5) {
      return change > 0 ? "Significant upward pressure driving rates higher." : "Significant downward pressure driving rates lower.";
    } else {
      return change > 0 ? "Strong economic forces pushing rates substantially higher." : "Strong economic forces pushing rates substantially lower.";
    }
  };

  // "Impact" is measured against today's fed funds rate when we have it. It was
  // previously measured against a literal 5.25 written at the call site, so the
  // badge described distance from a number that had nothing to do with today.
  const impactBaseline = liveMarket?.fed_funds_rate ?? null;

  // Calculate relative impact indicators
  const getImpactLevel = (value, baseline, isInverse = false) => {
    if (isMissing(baseline)) return { level: 'unknown', color: 'gray' };

    const diff = value - baseline;
    const absDiff = Math.abs(diff);
    
    if (absDiff < 0.1) return { level: 'neutral', color: 'gray' };
    
    const isPositive = isInverse ? diff < 0 : diff > 0;
    
    if (absDiff < 0.5) {
      return { level: isPositive ? 'low-positive' : 'low-negative', color: isPositive ? 'green' : 'red' };
    } else if (absDiff < 1.0) {
      return { level: isPositive ? 'medium-positive' : 'medium-negative', color: isPositive ? 'green' : 'red' };
    } else {
      return { level: isPositive ? 'high-positive' : 'high-negative', color: isPositive ? 'green' : 'red' };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBackToLanding}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <span>←</span>
              <span>Back to RateLens</span>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Rate Simulator</h1>
            <div></div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Simulation Controls */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-6">Economic Variables</h2>
            
            {/* Preset Scenarios */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">🎯 Quick Scenarios</h3>
              {!liveMarket && (
                <p className="text-xs text-gray-500 mb-3">
                  Live market conditions are unavailable, so the sliders start from a neutral position
                  and the "Current Market" scenario is not offered.
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(presetScenarios).map(([key, scenario]) => (
                  <button
                    key={key}
                    onClick={() => loadPresetScenario(key)}
                    className={`px-3 py-2 text-xs bg-white border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 transition-colors ${
                      key === 'current' ? 'relative' : ''
                    }`}
                  >
                    {scenario.name}
                    {key === 'current' && (
                      <div className="absolute -top-1 -right-1">
                        <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              {/* Live Data Link */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <button
                  onClick={onViewMarketData}
                  className="w-full px-3 py-2 text-xs bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <span>📡</span>
                  <span>View Live Market Data</span>
                  <span>→</span>
                </button>
                <p className="text-xs text-gray-500 mt-1 text-center">
                  See real-time data used in Current Market scenario
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              {/* Monetary Policy Section */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-lg font-semibold text-blue-700 mb-4">💰 Monetary Policy</h3>
                <div className="space-y-4">
                  {/* Fed Funds Rate */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Fed Funds Rate (%)
                      </label>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        getImpactLevel(simulationData.fed_funds_rate, impactBaseline).color === 'green' 
                          ? 'bg-green-100 text-green-700' 
                          : getImpactLevel(simulationData.fed_funds_rate, impactBaseline).color === 'red'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {getImpactLevel(simulationData.fed_funds_rate, impactBaseline).level.replace('-', ' ').replace('_', ' ')}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.25"
                      value={simulationData.fed_funds_rate}
                      onChange={(e) => handleInputChange('fed_funds_rate', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>0%</span>
                      <span className="font-medium">{simulationData.fed_funds_rate}%</span>
                      <span>10%</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      The interest rate banks charge each other for overnight loans. Higher rates typically lead to higher mortgage rates.
                    </p>
                  </div>

                  {/* QT/QE Policy */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      QT/QE Policy Intensity (0-1)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={simulationData.qt_intensity}
                      onChange={(e) => handleInputChange('qt_intensity', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>QE</span>
                      <span className="font-medium">{simulationData.qt_intensity}</span>
                      <span>QT</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      QE (bond buying) lowers rates by increasing demand. QT (bond selling) raises rates by reducing demand for bonds.
                    </p>
                  </div>
                </div>
              </div>

              {/* Inflation & Economic Data Section */}
              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="text-lg font-semibold text-red-700 mb-4">📈 Inflation & Economic Reports</h3>
                <div className="space-y-4">
                  {/* Inflation Rate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Inflation Rate (%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="8"
                      step="0.1"
                      value={simulationData.inflation_rate}
                      onChange={(e) => handleInputChange('inflation_rate', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>0%</span>
                      <span className="font-medium">{simulationData.inflation_rate}%</span>
                      <span>8%</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      How fast prices are rising. Higher inflation pressures the Fed to raise rates, which increases mortgage rates.
                    </p>
                  </div>

                  {/* Inflation Expectations */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Inflation Expectations (%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="8"
                      step="0.1"
                      value={simulationData.inflation_expectations}
                      onChange={(e) => handleInputChange('inflation_expectations', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>0%</span>
                      <span className="font-medium">{simulationData.inflation_expectations}%</span>
                      <span>8%</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      What markets expect inflation to be in the future. High expectations can drive rates up even before inflation rises.
                    </p>
                  </div>

                  {/* Unemployment Rate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unemployment Rate (%)
                    </label>
                    <input
                      type="range"
                      min="2"
                      max="12"
                      step="0.1"
                      value={simulationData.unemployment_rate}
                      onChange={(e) => handleInputChange('unemployment_rate', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>2%</span>
                      <span className="font-medium">{simulationData.unemployment_rate}%</span>
                      <span>12%</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      Percentage of people without jobs. Lower unemployment suggests a strong economy, potentially leading to higher rates.
                    </p>
                  </div>

                  {/* Economic Momentum */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Economic Reports Momentum (0-1)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={simulationData.economic_momentum}
                      onChange={(e) => handleInputChange('economic_momentum', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Weak</span>
                      <span className="font-medium">{simulationData.economic_momentum}</span>
                      <span>Strong</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">NFP, PMI, GDP, Consumer Confidence trends</p>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      Overall strength of economic data (jobs, manufacturing, spending). Strong momentum pressures rates higher.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bond & Treasury Market Section */}
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="text-lg font-semibold text-green-700 mb-4">🏛️ Treasury & Bond Markets</h3>
                <div className="space-y-4">
                  {/* 10-Year Treasury Yield */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      10-Year Treasury Yield (%)
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="8"
                      step="0.1"
                      value={simulationData.treasury_yield}
                      onChange={(e) => handleInputChange('treasury_yield', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>1%</span>
                      <span className="font-medium">{simulationData.treasury_yield}%</span>
                      <span>8%</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      Benchmark rate for US government 10-year bonds. Mortgage rates typically track this closely with a spread on top.
                    </p>
                  </div>

                  {/* Credit Spreads */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Credit Spreads (0-1)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={simulationData.credit_spreads}
                      onChange={(e) => handleInputChange('credit_spreads', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Tight</span>
                      <span className="font-medium">{simulationData.credit_spreads}</span>
                      <span>Wide</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Corporate bond stress levels</p>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      Risk premium investors demand for corporate vs. Treasury bonds. Wide spreads signal stress, raising all rates.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mortgage Market Section */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-lg font-semibold text-purple-700 mb-4">🏠 Mortgage Market</h3>
                <div className="space-y-4">
                  {/* MBS Spread */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      MBS Spread over Treasury (%)
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="4"
                      step="0.1"
                      value={simulationData.mbs_spread}
                      onChange={(e) => handleInputChange('mbs_spread', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>0.5%</span>
                      <span className="font-medium">{simulationData.mbs_spread}%</span>
                      <span>4%</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      Extra yield investors demand for mortgage-backed securities vs. Treasuries. Higher spreads = higher mortgage rates.
                    </p>
                  </div>

                  {/* MBS Demand/Pricing */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      MBS Demand/Pricing (0-1)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={simulationData.mbs_demand}
                      onChange={(e) => handleInputChange('mbs_demand', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Low Demand</span>
                      <span className="font-medium">{simulationData.mbs_demand}</span>
                      <span>High Demand</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Investor appetite for mortgage securities</p>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      How much investors want to buy mortgage bonds. High demand lowers rates, low demand raises rates.
                    </p>
                  </div>
                </div>
              </div>

              {/* Global Events Section */}
              <div className="border-l-4 border-orange-500 pl-4">
                <h3 className="text-lg font-semibold text-orange-700 mb-4">🌍 Global Events</h3>
                <div className="space-y-4">
                  {/* Geopolitical Risk */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Geopolitical Risk (0-1)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={simulationData.geopolitical_risk}
                      onChange={(e) => handleInputChange('geopolitical_risk', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Stable</span>
                      <span className="font-medium">{simulationData.geopolitical_risk}</span>
                      <span>Crisis</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Wars, trade tensions, political instability</p>
                    <p className="text-xs text-gray-600 mt-2 italic">
                      Global instability and uncertainty. High risk can either raise rates (inflation fears) or lower them (flight to safety).
                    </p>
                  </div>
                </div>
              </div>

              {/* Simulation Settings */}
              <div className="border-l-4 border-gray-500 pl-4">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">⚙️ Simulation Settings</h3>
                <div className="space-y-4">
                  {/* Time Horizon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time Horizon
                    </label>
                    <select
                      value={simulationData.time_horizon}
                      onChange={(e) => handleInputChange('time_horizon', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="1_day">1 Day</option>
                      <option value="1_week">1 Week</option>
                      <option value="1_month">1 Month</option>
                      <option value="3_months">3 Months</option>
                      <option value="6_months">6 Months</option>
                      <option value="1_year">1 Year</option>
                    </select>
                  </div>

                  {/* AI Analysis Toggle */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="ai_analysis"
                      checked={simulationData.include_ai_analysis}
                      onChange={(e) => handleInputChange('include_ai_analysis', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="ai_analysis" className="text-sm font-medium text-gray-700">
                      Include AI Analysis
                    </label>
                  </div>

                  {/* Run Simulation Button */}
                  <button
                    onClick={runSimulation}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-md hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    {loading ? 'Running Simulation...' : 'Run Rate Simulation'}
                  </button>

                  {/* Additional Action Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={resetToDefaults}
                      className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md text-gray-700 transition-colors"
                    >
                      🔄 Reset
                    </button>
                    
                    {results && (
                      <button
                        onClick={compareToTodaysForecast}
                        disabled={loading || !liveMarket}
                        title={liveMarket ? undefined : 'Live market conditions are unavailable'}
                        className="px-3 py-2 text-sm bg-green-100 hover:bg-green-200 border border-green-300 rounded-md text-green-700 transition-colors disabled:opacity-50"
                      >
                        📊 vs Today
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results Panel */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Results</h2>
              {results && (
                <button
                  onClick={exportResults}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded border text-gray-700 transition-colors"
                >
                  📄 Export
                </button>
              )}
            </div>
            
            {loading && (
              <div className="text-center py-12">
                <div className="inline-flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="text-lg text-gray-600">Running simulation...</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Analyzing economic variables and generating insights</p>
              </div>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <div className="flex items-center space-x-3">
                  <span className="text-red-600 text-xl">⚠️</span>
                  <div>
                    <h3 className="font-semibold text-red-800">Simulation Error</h3>
                    <p className="text-red-700 text-sm mt-1">{error}</p>
                    <button 
                      onClick={() => setError(null)}
                      className="text-xs text-red-600 hover:text-red-800 mt-2 underline"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {results ? (
              <div className="space-y-6">
                {/* Key Results */}
                {results.data && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                    <div className="text-center mb-6">
                      <h3 className="font-semibold text-blue-900 mb-3">Projected Mortgage Rate</h3>
                      <div className="flex items-center justify-center space-x-4">
                        <div className="text-5xl font-black text-blue-900">
                          {results.data.projected_mortgage_rate?.toFixed(3)}%
                        </div>
                        {results.data.rate_change && (
                          <div className="flex flex-col items-center">
                            {(() => {
                              const trend = getTrendIndicator(results.data.rate_change);
                              return (
                                <>
                                  <div className={`text-4xl ${trend.color}`}>
                                    {trend.icon}
                                  </div>
                                  <div className={`text-sm font-semibold ${trend.color}`}>
                                    {trend.trend}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      
                      {results.data.rate_change && (
                        <div className="mt-4 p-4 bg-white rounded-lg border border-blue-100">
                          <div className={`text-2xl font-bold mb-2 ${results.data.rate_change > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {results.data.rate_change > 0 ? '+' : ''}{results.data.rate_change?.toFixed(3)}% 
                            ({results.data.rate_change_bps > 0 ? '+' : ''}{results.data.rate_change_bps} bps)
                          </div>
                          <p className="text-sm text-gray-700 italic">
                            {getRateExplanation(results.data.projected_mortgage_rate, results.data.rate_change)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Comparison to Today's Forecast */}
                    {results.comparison && (
                      <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <h4 className="font-semibold text-yellow-900 mb-3 flex items-center">
                          <span className="mr-2">📊</span>
                          Comparison to Today's Market Forecast
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center">
                            <div className="text-sm text-gray-600">Your Scenario</div>
                            <div className="text-xl font-bold text-blue-900">
                              {results.data.projected_mortgage_rate?.toFixed(3)}%
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm text-gray-600">Today's Forecast</div>
                            <div className="text-xl font-bold text-gray-700">
                              {results.comparison.todays_forecast.projected_mortgage_rate?.toFixed(3)}%
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm text-gray-600">Difference</div>
                            <div className={`text-xl font-bold ${results.comparison.difference.rate_diff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {results.comparison.difference.rate_diff > 0 ? '+' : ''}{results.comparison.difference.rate_diff?.toFixed(3)}%
                              <div className="text-sm">
                                ({results.comparison.difference.rate_diff_bps > 0 ? '+' : ''}{Math.round(results.comparison.difference.rate_diff_bps)} bps)
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Confidence and Time Horizon */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="text-center p-3 bg-white rounded-lg border border-blue-100">
                        <div className="text-sm text-gray-600">Confidence Level</div>
                        <div className="text-xl font-semibold text-gray-800">
                          {results.data.projection_confidence ? (results.data.projection_confidence * 100).toFixed(0) : 'N/A'}%
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-blue-100">
                        <div className="text-sm text-gray-600">Time Horizon</div>
                        <div className="text-lg font-semibold text-gray-800">
                          {simulationData.time_horizon.replace('_', ' ').toUpperCase()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rate Breakdown */}
                {results.data?.rate_breakdown && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">📊 Rate Components</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div className="flex justify-between">
                        <span>Base Treasury:</span>
                        <span className="font-medium">{results.data.rate_breakdown.base_treasury?.toFixed(3)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>MBS Spread:</span>
                        <span className="font-medium">{results.data.rate_breakdown.spread_component?.toFixed(3)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fed Impact:</span>
                        <span className={`font-medium ${results.data.rate_breakdown.fed_impact > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {results.data.rate_breakdown.fed_impact > 0 ? '+' : ''}{results.data.rate_breakdown.fed_impact?.toFixed(3)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Inflation Premium:</span>
                        <span className={`font-medium ${results.data.rate_breakdown.inflation_premium > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {results.data.rate_breakdown.inflation_premium > 0 ? '+' : ''}{results.data.rate_breakdown.inflation_premium?.toFixed(3)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Risk Premium:</span>
                        <span className={`font-medium ${results.data.rate_breakdown.risk_premium > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {results.data.rate_breakdown.risk_premium > 0 ? '+' : ''}{results.data.rate_breakdown.risk_premium?.toFixed(3)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>QT Impact:</span>
                        <span className={`font-medium ${results.data.rate_breakdown.qt_impact > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {results.data.rate_breakdown.qt_impact > 0 ? '+' : ''}{results.data.rate_breakdown.qt_impact?.toFixed(3)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Analysis */}
                {results.data?.ai_analysis && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                    <h3 className="font-semibold text-purple-900 mb-4 flex items-center">
                      <span className="text-xl mr-2">🧠</span>
                      AI Analysis
                    </h3>
                    
                    {results.data.ai_analysis.ai_insights?.quick_summary && (
                      <div className="mb-4 p-4 bg-white rounded-lg border border-purple-100">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                          <div className="text-center">
                            <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                              <span>Impact Level</span>
                              <div className="ml-1 group relative">
                                <span className="text-gray-400 cursor-help">ℹ️</span>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 z-10">
                                  Overall strength of economic forces affecting rates
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                            <div className={`font-bold text-lg ${
                              results.data.ai_analysis.ai_insights.quick_summary.impact_level === 'High' ? 'text-red-600' :
                              results.data.ai_analysis.ai_insights.quick_summary.impact_level === 'Medium' ? 'text-yellow-600' :
                              'text-green-600'
                            }`}>
                              {results.data.ai_analysis.ai_insights.quick_summary.impact_level}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {results.data.ai_analysis.ai_insights.quick_summary.impact_level === 'High' ? 'Strong market forces' :
                               results.data.ai_analysis.ai_insights.quick_summary.impact_level === 'Medium' ? 'Moderate influence' :
                               'Minimal market impact'}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                              <span>Direction</span>
                              <div className="ml-1 group relative">
                                <span className="text-gray-400 cursor-help">ℹ️</span>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 z-10">
                                  Whether rates are expected to move up or down
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                            <div className={`font-bold text-lg flex items-center justify-center ${
                              results.data.ai_analysis.ai_insights.quick_summary.rate_direction === 'Higher' ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {results.data.ai_analysis.ai_insights.quick_summary.rate_direction === 'Higher' ? '↗️' : '↘️'}
                              <span className="ml-1">{results.data.ai_analysis.ai_insights.quick_summary.rate_direction}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {results.data.ai_analysis.ai_insights.quick_summary.rate_direction === 'Higher' ? 'Upward pressure' : 'Downward pressure'}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                              <span>Magnitude</span>
                              <div className="ml-1 group relative">
                                <span className="text-gray-400 cursor-help">ℹ️</span>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 z-10">
                                  Size of expected rate change in basis points (1% = 100 bps)
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                            <div className="font-bold text-lg text-gray-800">
                              {results.data.ai_analysis.ai_insights.quick_summary.magnitude_bps} bps
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {Math.abs(results.data.ai_analysis.ai_insights.quick_summary.magnitude_bps) < 25 ? 'Small change' :
                               Math.abs(results.data.ai_analysis.ai_insights.quick_summary.magnitude_bps) < 50 ? 'Moderate change' :
                               'Large change'}
                            </div>
                          </div>
                        </div>
                        
                        {/* Additional explanation */}
                        <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                          <div className="text-xs text-purple-700">
                            <strong>🎯 Quick Guide:</strong> 
                            <span className="ml-2">
                              <strong>Impact Level</strong> shows how strong the economic forces are. 
                              <strong>Direction</strong> indicates if rates go up ↗️ or down ↘️. 
                              <strong>Magnitude</strong> measures the size of change (25 bps = 0.25%).
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {results.data.ai_analysis.ai_insights?.full_analysis && (
                      <div className="text-sm text-purple-800 bg-white p-4 rounded-lg border border-purple-100 max-h-64 overflow-y-auto">
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {results.data.ai_analysis.ai_insights.full_analysis}
                        </div>
                      </div>
                    )}
                    
                    {results.data.ai_analysis.error && (
                      <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                        AI Analysis unavailable: {results.data.ai_analysis.error}
                      </div>
                    )}
                  </div>
                )}

              </div>
            ) : !loading && (
              <div className="text-center py-16 text-gray-500">
                <div className="text-6xl mb-4">🎯</div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">Ready to Simulate</h3>
                <p className="text-sm max-w-sm mx-auto mb-6">
                  Adjust the economic parameters above and run a simulation to see how mortgage rates respond to different scenarios.
                </p>
                <div className="text-xs text-gray-400 space-y-1">
                  <p>• Use preset scenarios for quick testing</p>
                  <p>• All variables have real-time impact indicators</p>
                  <p>• AI analysis provides detailed insights</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Simulator;
